import { useState, useRef, useCallback, useEffect } from 'react';
import useBank from './useBank';
import { createStringDeck, shuffleDeck } from '../utils/CardUtils';
import { BET_DENOMINATIONS } from '../utils/GameConstants';

const getCardValue = (card) => {
  const value = card.slice(1);
  if (value === 'A') return 11;
  if (['K', 'Q', 'J'].includes(value)) return 10;
  return parseInt(value, 10);
};

export const calculateHandValues = (hand) => {
  let totalValue = hand.reduce((sum, card) => sum + getCardValue(card), 0);
  let aceCount = hand.filter(card => card.slice(1) === 'A').length;
  let softValue = totalValue;
  while (softValue > 21 && aceCount > 0) {
    softValue -= 10;
    aceCount -= 1;
  }
  return { softValue, totalValue };
};

const shouldDealerHit = (hand) => {
  const { softValue } = calculateHandValues(hand);
  return softValue < 17;
};

export const useBlackJackGame = () => {
  const { user, depositMoney, withdrawMoney, refreshUser } = useBank();
  const [deck, setDeck] = useState(shuffleDeck(createStringDeck()));
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [dealerHandRevealed, setDealerHandRevealed] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [betAmount, setBetAmount] = useState(0);
  const [totalBet, setTotalBet] = useState(0);
  const [canDoubleDown, setCanDoubleDown] = useState(true);
  const [hasDoubledDown, setHasDoubledDown] = useState(false);

  const gameStateRef = useRef({
    isFinalizingGame: false,
    playerHand: [],
    dealerHand: [],
    betAmount: 0,
    hasDoubledDown: false,
  });

  const betDenominations = BET_DENOMINATIONS;

  useEffect(() => {
    if (hasDoubledDown) {
      setTotalBet(prevTotalBet => prevTotalBet * 2);
    }
  }, [hasDoubledDown]);

  const finalizeGame = useCallback(async (finalPlayerHand, finalDealerHand) => {
    if (gameStateRef.current.isFinalizingGame === true) return;
    const playerValue = calculateHandValues(finalPlayerHand).softValue;
    const dealerValue = calculateHandValues(finalDealerHand).softValue;
    const winAmount = gameStateRef.current.betAmount * 2;
    
    console.log('Player hand:', finalPlayerHand);
    console.log('Player value:', playerValue);
    console.log('Dealer hand:', finalDealerHand);
    console.log('Dealer value:', dealerValue);
    console.log('Total bet:', gameStateRef.current.betAmount);
    console.log('Has doubled down:', gameStateRef.current.hasDoubledDown);
    console.log('Win amount:', winAmount);
  
    setGameOver(true);
    if (gameStateRef.current.hasDoubledDown) {
      console.log('The user has doubled down in this game.');
    }
    if (playerValue > 21) {
      setMessage('You busted! Dealer wins.');
    } else if (dealerValue > 21) {
      console.log('Dealer busted, calling withdrawMoney');
      await withdrawMoney(winAmount);
      setMessage(`Dealer busted! You won 💴 ${winAmount}!`);
    } else if (playerValue > dealerValue) {
      console.log('Player wins, calling withdrawMoney');
      await withdrawMoney(winAmount);
      setMessage(`You won 💴 ${winAmount}!`);
    } else if (playerValue < dealerValue) {
      setMessage('Dealer wins.');
    } else {
      console.log('Push, calling withdrawMoney');
      await withdrawMoney(gameStateRef.current.betAmount);
      setMessage('Push.');
    }
  
    gameStateRef.current.isFinalizingGame = false;
    console.log('Refreshing user after finalizing game');
    await refreshUser();
  }, [withdrawMoney, refreshUser]);

  const dealerMove = useCallback(async () => {
    if (gameStateRef.current.isFinalizingGame) return;
    gameStateRef.current.isFinalizingGame = true;
    let newDealerHand = [...dealerHand];
    const dealerTurn = async () => {
      while (shouldDealerHit(newDealerHand)) {
        newDealerHand.push(deck.pop());
        setDealerHand([...newDealerHand]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      finalizeGame(gameStateRef.current.playerHand, newDealerHand);
    };
    setTimeout(dealerTurn, 1000);
  }, [dealerHand, finalizeGame, deck]);

  const startGame = async () => {
    if (user.money >= betAmount) {
      console.log('Starting game, calling depositMoney');
      await depositMoney(betAmount);
      const newPlayerHand = [deck.pop(), deck.pop()];
      const newDealerHand = [deck.pop(), deck.pop()];
      setPlayerHand(newPlayerHand);
      setDealerHand(newDealerHand);
      setDealerHandRevealed(false);
      setGameStarted(true);
      setGameOver(false);
      setMessage('');
      setCanDoubleDown(true);
      setHasDoubledDown(false);
      setTotalBet(betAmount);
      gameStateRef.current = {
        isFinalizingGame: false,
        playerHand: newPlayerHand,
        dealerHand: newDealerHand,
        betAmount: betAmount,
        hasDoubledDown: false,
      };
      console.log('Refreshing user after starting game');
      await refreshUser();
    } else {
      setMessage('Insufficient funds to place the bet.');
    }
  };

  const playerHit = async () => {
    if (gameOver || gameStateRef.current.isFinalizingGame) return;
    
    const newPlayerHand = [...playerHand, deck.pop()];
    setPlayerHand(newPlayerHand);
    gameStateRef.current.playerHand = newPlayerHand;
  
    await new Promise(resolve => setTimeout(resolve, 100));
  
    const { softValue } = calculateHandValues(newPlayerHand);
    setCanDoubleDown(false);
  
    if (softValue > 21) {
      gameStateRef.current.isFinalizingGame = true;
      setMessage('You busted! Dealer wins.');
      setTimeout(() => finalizeGame(newPlayerHand, gameStateRef.current.dealerHand), 1000);
    } else {
      setMessage(`Your new total is ${softValue}.`);
    }
  };
  

  const playerStand = () => {
    setCanDoubleDown(false);
    if (!gameOver && !gameStateRef.current.isFinalizingGame) {
      setTimeout(() => {
        setDealerHandRevealed(true);
        setTimeout(() => dealerMove(), 1500);
      }, 1000);
    }
  };

  const doubleDown = async () => {
    if (user.money >= betAmount && !gameStateRef.current.isFinalizingGame) {
      console.log('Doubling down, calling depositMoney');
      await depositMoney(betAmount);
      setPlayerHand(prevHand => {
        const newHand = [...prevHand, deck.pop()];
        setTotalBet(prevTotalBet => prevTotalBet * 2);
        setCanDoubleDown(false);
        setHasDoubledDown(true);
        console.log('Setting hasDoubledDown to true');
        
        gameStateRef.current = {
          ...gameStateRef.current,
          playerHand: newHand,
          betAmount: betAmount * 2,
          hasDoubledDown: true
        };

        const { softValue } = calculateHandValues(newHand);
        if (softValue > 21) {
          gameStateRef.current.isFinalizingGame = true;
          setMessage('You busted! Dealer wins.');
          setTimeout(() => finalizeGame(newHand, gameStateRef.current.dealerHand), 1000);
        } else {
          setTimeout(() => {
            setDealerHandRevealed(true);
            setTimeout(dealerMove, 1500);
          }, 1000);
        }
        return newHand;
      });
      console.log('Refreshing user after doubling down');
      await refreshUser();
    } else {
      setMessage('Insufficient funds to double down.');
    }
  };

  const resetGame = () => {
    setDeck(shuffleDeck(createStringDeck()));
    setPlayerHand([]);
    setDealerHand([]);
    setDealerHandRevealed(false);
    setGameOver(false);
    setMessage('');
    setGameStarted(false);
    setBetAmount(0);
    setTotalBet(0);
    setHasDoubledDown(false);
    gameStateRef.current = {
      isFinalizingGame: false,
      playerHand: [],
      dealerHand: [],
      betAmount: 0,
      hasDoubledDown: false,
    };
  };

  const handleBet = (amount) => {
    setBetAmount(prevBetAmount => {
      const newBetAmount = prevBetAmount + amount;
      gameStateRef.current.betAmount = newBetAmount;
      return newBetAmount;
    });
  };

  const clearBet = () => {
    setBetAmount(0);
    gameStateRef.current.betAmount = 0;
  };

  const displayDealerValue = () => {
    if (!dealerHandRevealed) {
      const visibleCards = dealerHand.slice(1);
      const { softValue } = calculateHandValues(visibleCards);
      return softValue;
    }
    const { softValue } = calculateHandValues(dealerHand);
    return softValue;
  };

  return {
    user,
    playerHand,
    dealerHand,
    dealerHandRevealed,
    gameOver,
    message,
    gameStarted,
    betAmount,
    totalBet,
    betDenominations,
    startGame,
    playerHit,
    playerStand,
    doubleDown,
    resetGame,
    handleBet,
    clearBet,
    canDoubleDown,
    displayDealerValue,
    hasDoubledDown,
  };
};